"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { cardToCash, CASH_DISCOUNT_PCT } from "@/lib/utils/pricing";

export default function ZeroFeesReceiptScroll() {
  const { t } = useLang();

  // Menu prices ARE the card prices (that's how Redy's dual pricing works).
  // The cash receipt shows a "Cash discount (x%)" line, computed once on the total.
  const items = [
    { name: "Birria Taco × 2", card: 9.50 },
    { name: "Agua Fresca", card: 4.25 },
    { name: "Elote Loco", card: 5.75 },
  ];

  const pct = CASH_DISCOUNT_PCT;
  const pctLabel = `${Math.round(pct * 100)}%`;
  const cardTotal = Math.round(items.reduce((s, i) => s + i.card, 0) * 100) / 100;
  const cashTotal = cardToCash(cardTotal, pct);
  const cashDiscount = Math.round((cardTotal - cashTotal) * 100) / 100;

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        {/* Section headline */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#00A876" }}>
            {t("Side by side", "Lado a lado")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal max-w-[800px] mx-auto">
            {t("The math, ", "La cuenta, ")}
            <span className="text-redy-charcoal/30">
              {t("side by side.", "lado a lado.")}
            </span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary max-w-[520px] mx-auto mt-6">
            {t(
              "The menu shows the card price. Pay cash and the receipt shows the discount. The customer picks.",
              "El menú muestra el precio con tarjeta. Si pagan en efectivo, el recibo muestra el descuento. El cliente elige."
            )}
          </p>
        </motion.div>

        {/* Two receipts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[820px] mx-auto">
          {/* Cash receipt */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="bg-[#FAFAF8] rounded-[24px] p-8 border border-black/5"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          >
            <div className="text-center pb-4 border-b border-dashed border-black/15 mb-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-redy-secondary/70">
                {t("If they pay cash", "Si pagan en efectivo")}
              </div>
              <div className="text-[18px] font-black mt-1 text-redy-charcoal">{t("CASH", "EFECTIVO")}</div>
            </div>

            <div className="space-y-3 text-[13px]">
              {items.map((item, i) => (
                <div key={i} className="flex justify-between text-redy-charcoal">
                  <span>{item.name}</span>
                  <span className="tabular-nums">${item.card.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-dashed border-black/15 my-4" />

            <div className="space-y-1.5 text-[12px]">
              <div className="flex justify-between text-redy-secondary">
                <span>{t("Subtotal", "Subtotal")}</span>
                <span className="tabular-nums">${cardTotal.toFixed(2)}</span>
              </div>
              <motion.div
                initial={{ backgroundColor: "rgba(0,200,150,0)" }}
                whileInView={{ backgroundColor: "rgba(0,200,150,0.12)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex justify-between font-bold rounded px-2 py-1 -mx-2"
                style={{ color: "#00A876" }}
              >
                <span>{t(`Cash discount (${pctLabel})`, `Descuento en efectivo (${pctLabel})`)}</span>
                <span className="tabular-nums">-${cashDiscount.toFixed(2)}</span>
              </motion.div>
            </div>

            <div className="border-t border-dashed border-black/15 my-4" />

            <div className="flex justify-between items-baseline">
              <span className="text-[12px] font-bold uppercase tracking-wider text-redy-secondary/70">
                {t("Total", "Total")}
              </span>
              <span className="text-[28px] font-black text-redy-charcoal tabular-nums">
                ${cashTotal.toFixed(2)}
              </span>
            </div>

            <div className="mt-4 text-center text-[10px] uppercase tracking-widest font-bold" style={{ color: "#00A876" }}>
              {t("Cash discount applied", "Descuento en efectivo aplicado")}
            </div>
          </motion.div>

          {/* Card receipt */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="bg-white rounded-[24px] p-8 border border-black/5 shadow-[0_20px_60px_rgba(0,0,0,0.04)]"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          >
            <div className="text-center pb-4 border-b border-dashed border-black/15 mb-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-redy-secondary/70">
                {t("If they pay card", "Si pagan con tarjeta")}
              </div>
              <div className="text-[18px] font-black mt-1 text-redy-charcoal">{t("CARD", "TARJETA")}</div>
            </div>

            <div className="space-y-3 text-[13px]">
              {items.map((item, i) => (
                <div key={i} className="flex justify-between text-redy-charcoal">
                  <span>{item.name}</span>
                  <span className="tabular-nums">${item.card.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-dashed border-black/15 my-4" />

            <div className="flex justify-between items-baseline">
              <span className="text-[12px] font-bold uppercase tracking-wider text-redy-secondary/70">
                {t("Total", "Total")}
              </span>
              <span className="text-[28px] font-black text-redy-charcoal tabular-nums">
                ${cardTotal.toFixed(2)}
              </span>
            </div>

            <div className="mt-4 text-center text-[10px] uppercase tracking-widest font-bold text-redy-secondary/60">
              {t("Menu price — covers the processing cost", "Precio del menú — cubre el procesamiento")}
            </div>
          </motion.div>
        </div>

        {/* Caption */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center text-[14px] font-medium text-redy-secondary mt-12 max-w-[560px] mx-auto"
        >
          {t(
            "Card payers pay the menu price, which is designed to cover the processing cost. Cash payers get the discount. Example prices.",
            "Quien paga con tarjeta paga el precio del menú, pensado para cubrir el costo de procesamiento. Quien paga en efectivo recibe el descuento. Precios de ejemplo."
          )}
        </motion.p>
      </div>
    </section>
  );
}
